import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, DefaultFailureAction, DefaultSuccessAction, initMapFactory, queryMapFactory } from "../common/reducers/utilities";
import { aggregatingFetch } from "../common/hooks/networkHook";
import { dateRangeOverlap } from "../common/utils/dateUtils";
import { ensureArray } from "../common/utils/arrayUtilities";
import { markFetched, registerEntityBasedFetch } from "../common/hooks/initHook";

const compareShiftInterests = (si1, si2) => si1.memberid === si2.memberid && si1.shiftid === si2.shiftid;

const shiftInterestSlice = createSlice({
    name: 'shiftInterest',
    initialState: {
        data: []
    },
    reducers: {
        set: (state, action) => {
            action.payload.keys.forEach(key => {
                let idx = state.data.findIndex(s => s.shiftid === key.shiftid && s.memberid === key.memberid);
                if (idx >= 0) {
                    state.data.splice(idx, 1);
                }    
            });
            shiftInterestSlice.caseReducers.addOrUpdate(state, { payload: action.payload.data });
        },
        updateShift: (state, action) => {
            ensureArray(action.payload).forEach(s => {
                state.data.forEach(i => {
                    if (i.shiftid === s.id) {
                        i.start = s.start;
                        i.end = s.end;
                    }
                });
            });
        },
        addOrUpdate: (state, action) => {
            ensureArray(action.payload).forEach(i => {
                let idx = state.data.findIndex(i2 => compareShiftInterests(i, i2));
                if (idx >= 0) {
                    state.data.splice(idx, 1, i);
                } else {
                    state.data.push(i);
                }
            })
        },
        remove: (state, action) => {
            ensureArray(action.payload).forEach(interest => {
                const idx = state.data.findIndex(i => compareShiftInterests(i, interest));
                if (idx >= 0) {
                    state.data.splice(idx, 1);
                }
            })
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => shiftInterestSlice.getInitialState());
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'shiftInterest', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'shiftInterest');


const fetchShiftInterst = aggregatingFetch(
    (memberid, shiftid) => network => network.post('/api/v2/memberShiftInterestSearch', { memberid, shiftid }, data => shiftInterestSlice.actions.set({ data, keys: [{ memberid, shiftid }] }), visualizeFailure('get')),
    keys => network => network.post('/api/v2/memberShiftInterestSearch', keys.map(([memberid, shiftid]) => ({ memberid, shiftid })), data => shiftInterestSlice.actions.set({ data, keys: keys.map(([memberid, shiftid]) => ({memberid, shiftid})) }), visualizeFailure('get'))
);
const fetchShiftInterestsInRange = (start, end) => network => network.post('/api/v2/membershiftInterestSearch', { start, end }, shiftInterestSlice.actions.addOrUpdate, visualizeFailure('get'));
const fetchMemberShiftInterestsInRange = (memberid, start, end) => network => network.post('/api/v2/membershiftInterestSearch', { memberid, start, end }, shiftInterestSlice.actions.addOrUpdate, visualizeFailure('get'));
// #endregion

export const createMemberShiftInterest = (network, interest) => network.post('/api/v1/memberShiftInterest', interest, shiftInterestSlice.actions.addOrUpdate, visualizeFailure('create'));
export const removeMemberShiftInterest = (network, memberid, shiftid) => network.delete(`/api/v1/memberShiftInterest/${encodeURIComponent(memberid)}/${encodeURIComponent(shiftid)}`, shiftInterestSlice.actions.remove, visualizeFailure('remove'));
// #region selectors
const selectShiftInterestData = state => state.shiftInterest.data;
const selectShiftInterest = (memberid, shiftid) => createSelector([selectShiftInterestData], interests => interests.filter(i => i.memberid === memberid && i.shiftid === shiftid))
const selectShiftInterestInRange = (start, end) => createSelector([selectShiftInterestData], interests => interests.filter(i => dateRangeOverlap(i, { start, end })))
const selectMemberShiftInterestInRange = (memberid, start, end) => createSelector([selectShiftInterestData], interests => interests.filter(i => i.memberid === memberid && dateRangeOverlap(i, { start, end })));
// #endregion

const listen = {
    memberShiftInterestCreated: (interest, { dispatch, websocket }) => {
        markFetched(websocket, 'shift_interest', interest.memberid, interest.shiftid);
        dispatch(shiftInterestSlice.actions.addOrUpdate(interest))
    },
    memberShiftInterestUpdated: (interest, { dispatch }) => dispatch(shiftInterestSlice.actions.addOrUpdate(interest)),
    memberShiftInterestRemoved: (interest, { dispatch }) => dispatch(shiftInterestSlice.actions.remove(interest)),
    shiftAdminUpdate: (shift, { dispatch }) => dispatch(shiftInterestSlice.actions.updateShift(shift))
}
const shiftInterestInitByID = initMapFactory(fetchShiftInterst, listen);
registerEntityBasedFetch('shift_interest', shiftInterestInitByID)
const cleanup = { root: shiftInterestSlice.name, all: selectShiftInterestData, removeAll: shiftInterestSlice.actions.remove, remove: shiftInterestSlice.actions.remove };
export const [useShiftInterest] = dataHookFactory(selectShiftInterest, shiftInterestInitByID, { cleanup });
export const [useShiftInterestInRange] = dataHookFactory(selectShiftInterestInRange, queryMapFactory(fetchShiftInterestsInRange, ['memberid', 'shiftid'], shiftInterestInitByID, listen), { cleanup });
export const [useMemberShiftInterestInRange] = dataHookFactory(selectMemberShiftInterestInRange, queryMapFactory(fetchMemberShiftInterestsInRange, ['memberid', 'shiftid'], shiftInterestInitByID, listen), { cleanup });

const reducer = { name: shiftInterestSlice.name, reducer: shiftInterestSlice.reducer }
export default reducer;